import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccordionModule } from 'primeng/accordion';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TranslateModule } from '@ngx-translate/core';
import { SupportModalComponent } from './support-modal/support-modal.component';

interface FaqSection {
  title: string;
  items: FaqItem[];
}

interface FaqItem {
  question: string;
  answer: string;
  isLast?: boolean;
}

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AccordionModule,
    CardModule,
    DividerModule,
    ButtonModule,
    RippleModule,
    TranslateModule,
    SupportModalComponent
  ]
})
export class FaqComponent implements OnInit {
  @ViewChild(SupportModalComponent) supportModal!: SupportModalComponent;

  faqSections: FaqSection[] = [
    {
      title: 'Informazioni Generali',
      items: [
        {
          question: 'Cos\'è PMO2?',
          answer: 'PMO2 è un\'applicazione web sviluppata in Angular che gestisce e monitora interventi e schede tecniche relative a opere pubbliche.',
          isLast: false
        }
      ]
    },
    {
      title: 'Accesso e Autenticazione',
      items: [
        {
          question: 'Come posso accedere all\'applicazione?',
          answer: 'Per accedere all\'applicazione è necessario effettuare il login tramite Auth0. Il sistema supporta diversi livelli di accesso (utente, admin, super admin).',
          isLast: true
        },
        {
          question: 'Ho dimenticato la password, come posso recuperarla?',
          answer: 'Utilizza la funzione "Password dimenticata" presente nella pagina di login. Riceverai un\'email con le istruzioni per reimpostare la password. Importante: tutti gli utenti dovranno aggiornare la password attraverso questo sistema.',
          isLast: true
        }
      ]
    },
    {
      title: 'Gestione Schede',
      items: [
        {
          question: 'Come posso creare una nuova scheda?',
          answer: 'È possibile creare una nuova scheda utilizzando il componente "Nuova Scheda" presente nell\'interfaccia.',
          isLast: false
        },
        {
          question: 'Quali informazioni contiene una scheda?',
          answer: 'Ogni scheda contiene: codice identificativo, bacino di riferimento, comune, oggetto dell\'intervento, accessibilità, frequenza degli interventi, anno dell\'ultimo e prossimo intervento, importo dell\'ultimo intervento e allegati.',
          isLast: true
        }
      ]
    },
    {
      title: 'Richieste',
      items: [
        {
          question: 'Quando posso inserire una nuova richiesta?',
          answer: 'Potrai inserire una nuova richiesta solo dopo che l\'Unione Montana ti comunicherà ufficialmente l\'apertura del periodo di presentazione. Generalmente questo avviene in preparazione dell\'anno successivo.',
          isLast: false
        },
        {
          question: 'Quante richieste posso presentare?',
          answer: 'È possibile presentare una richiesta per anno e per ente. Ogni richiesta è un contenitore che può includere più interventi.',
          isLast: true
        }
      ]
    },
    {
      title: 'Gestione Interventi',
      items: [
        {
          question: 'Come posso visualizzare gli interventi?',
          answer: 'Gli interventi sono visualizzabili sia in formato tabellare che su mappa interattiva.',
          isLast: false
        },
        {
          question: 'Quali azioni posso compiere sugli interventi?',
          answer: 'È possibile: creare nuovi interventi, rimodulare richieste, confermare finanziamenti, rendicontare, visualizzare lo storico e modificare interventi esistenti.',
          isLast: true
        }
      ]
    },
    {
      title: 'Rendicontazione',
      items: [
        {
          question: 'Quando posso iniziare a rendicontare per l\'anno corrente?',
          answer: 'Puoi iniziare la rendicontazione solo dopo che la richiesta è passata allo stato "Finanziate" e l\'Unione Montana ha comunicato l\'avvio della fase di rendicontazione.',
          isLast: false
        },
        {
          question: 'Come funziona la rimodulazione?',
          answer: 'La rimodulazione serve quando l\'importo richiesto è superiore al contributo approvato. Ad esempio, se hai chiesto 15.000€ ma sono stati approvati solo 10.000€, dovrai rimodulare tutti gli interventi riducendoli proporzionalmente per rientrare nel budget approvato.',
          isLast: false
        },
        {
          question: 'Come funziona la gestione degli allegati?',
          answer: 'È possibile modificare e sostituire i file assegnati a una rendicontazione in qualsiasi momento, anche dopo averla creata. Non è necessario eliminare e ricreare la rendicontazione per aggiornare la documentazione allegata.<br><br><strong>Per modificare gli allegati:</strong><ul><li>Accedere alla rendicontazione desiderata</li><li>Cliccare sulla sezione "Allegati" o "Documenti"</li><li>Aggiungere nuovi file, rimuovere quelli esistenti o sostituirli secondo necessità</li><li>Salvare le modifiche</li></ul>',
          isLast: false
        },
        {
          question: 'È possibile modificare l\'importo di una rendicontazione?',
          answer: 'Sì, è possibile modificare l\'importo di una rendicontazione esistente. Questa funzionalità consente di correggere eventuali errori o di aggiornare l\'importo senza dover ricreare la rendicontazione da zero.<br><br><strong>Per modificare l\'importo:</strong><ul><li>Aprire la rendicontazione da modificare</li><li>Individuare il campo "Importo" o "Valore"</li><li>Inserire il nuovo importo</li><li>Salvare le modifiche</li></ul><br><i>Si consiglia di verificare che l\'importo modificato sia coerente con la documentazione allegata e con le normative di riferimento del progetto.</i>',
          isLast: true
        },
      ]
    },
    {
      title: 'Funzionalità Mappa',
      items: [
        {
          question: 'Come funziona la visualizzazione sulla mappa?',
          answer: 'La mappa mostra: confini dei comuni, posizione degli interventi, geometrie delle schede, popup informativi al click e tooltip al passaggio del mouse.',
          isLast: false
        },
        {
          question: 'Posso filtrare gli elementi sulla mappa?',
          answer: 'Sì, è possibile filtrare gli elementi per: comune, bacino, tipo di intervento e stato.',
          isLast: true
        }
      ]
    },
    {
      title: 'Funzionalità Tecniche',
      items: [
        {
          question: 'Quali tipi di file posso caricare?',
          answer: 'L\'applicazione supporta: file PDF, file Excel, file GeoJSON e immagini.',
          isLast: false
        },
        {
          question: 'Come posso esportare i dati?',
          answer: 'È possibile esportare i dati in formato Excel per: schede, interventi e report specifici.',
          isLast: true
        }
      ]
    },
    {
      title: 'Supporto',
      items: [
        {
          question: 'Come posso ottenere aiuto?',
          answer: 'È possibile: utilizzare il sistema di supporto integrato, contattare l\'amministratore di sistema o consultare la documentazione tecnica.',
          isLast: false
        },
        {
          question: 'Cosa fare in caso di problemi?',
          answer: 'In caso di problemi è consigliabile: verificare la connessione internet, controllare le credenziali di accesso, pulire la cache del browser e contattare il supporto tecnico.',
          isLast: true
        }
      ]
    }
  ];

  constructor() {}

  ngOnInit(): void {}

  openSupportModal() {
    this.supportModal.show();
  }
}
